// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect } from 'react';
import MissionManager from '../../utils/game/MissionManager';
import Button from '../Button';
import './MissionsTab.css';

/**
 * Missions Tab Component
 * 
 * Displays active missions and mission board access
 */
const MissionsTab = ({ currentSystem, allSystems, onOpenMissionBoard }) => {
  const [activeMissions, setActiveMissions] = useState([]);
  const [selectedMission, setSelectedMission] = useState(null);

  useEffect(() => {
    // Update active missions
    const missions = MissionManager.getActiveMissions();
    setActiveMissions(missions);

    // Update mission timers every second
    const interval = setInterval(() => {
      MissionManager.updateMissions(1); // 1 second
      setActiveMissions([...MissionManager.getActiveMissions()]);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleSelectMission = (mission) => {
    setSelectedMission(mission);
  };

  const handleAbandonMission = (missionId) => {
    if (window.confirm('Are you sure you want to abandon this mission?')) {
      MissionManager.failMission(missionId, 'Mission abandoned');
      setActiveMissions(MissionManager.getActiveMissions());
      setSelectedMission(null);
    }
  };

  return (
    <div className="missions-tab">
      {/* Header */}
      <div className="missions-tab-header">
        <div className="missions-tab-title">ACTIVE MISSIONS</div>
        <Button onClick={onOpenMissionBoard} size="small">
          MISSION BOARD
        </Button>
      </div>

      {/* Content */}
      <div className="missions-tab-content">
        {activeMissions.length === 0 ? (
          <div className="missions-empty">
            <div className="missions-empty-icon">📋</div>
            <div className="missions-empty-text">No active missions</div>
            <div className="missions-empty-hint">
              Visit a station and check the Mission Board to accept missions
            </div>
            <Button onClick={onOpenMissionBoard}>
              OPEN MISSION BOARD
            </Button>
          </div>
        ) : (
          <div className="missions-grid">
            {/* Mission List */}
            <div className="missions-list">
              {activeMissions.map(mission => (
                <div
                  key={mission.id}
                  className={`mission-item ${selectedMission?.id === mission.id ? 'selected' : ''}`}
                  onClick={() => handleSelectMission(mission)}
                >
                  <div className="mission-item-header">
                    <div className="mission-item-title">{mission.title}</div>
                    <div 
                      className="mission-item-time"
                      style={{ 
                        color: mission.timeRemaining < 60 ? '#ff4444' : 
                               mission.timeRemaining < 180 ? '#ffaa00' : '#00ff88'
                      }}
                    >
                      ⏱ {MissionManager.formatTimeRemaining(mission.timeRemaining)}
                    </div>
                  </div>
                  <div className="mission-item-type">{mission.type.toUpperCase()}</div>
                  <div className="mission-item-reward">
                    💰 {mission.reward} CR | ⭐ +{mission.reputation} REP
                  </div>
                </div>
              ))}
            </div>

            {/* Mission Details */}
            <div className="missions-details">
              {selectedMission ? (
                <>
                  <div className="missions-details-header">
                    <div className="missions-details-title">{selectedMission.title}</div>
                    <div 
                      className="missions-details-difficulty"
                      style={{ 
                        color: selectedMission.difficultyColor,
                        borderColor: selectedMission.difficultyColor
                      }}
                    >
                      {selectedMission.difficulty}
                    </div>
                  </div>

                  <div className="missions-details-description">
                    {selectedMission.description}
                  </div>

                  <div className="missions-details-objectives">
                    <div className="objectives-title">OBJECTIVES:</div>
                    {selectedMission.objectives.map((obj, idx) => (
                      <div key={idx} className="objective-item">
                        <span className="objective-bullet">▸</span>
                        {obj}
                      </div>
                    ))}
                  </div>

                  {/* Mission Progress */}
                  {selectedMission.type === 'delivery' && (
                    <div className="missions-details-progress">
                      <div className="progress-title">PROGRESS:</div>
                      <div className="progress-item">
                        <span className="progress-label">Destination:</span>
                        <span className="progress-value">{selectedMission.destination}</span>
                      </div>
                    </div>
                  )}
                  {selectedMission.type === 'combat' && (
                    <div className="missions-details-progress">
                      <div className="progress-title">PROGRESS:</div>
                      <div className="progress-item">
                        <span className="progress-label">Kills:</span>
                        <span className="progress-value">
                          {selectedMission.killCount || 0} / {selectedMission.enemyCount}
                        </span>
                      </div>
                    </div>
                  )}
                  {selectedMission.type === 'exploration' && (
                    <div className="missions-details-progress">
                      <div className="progress-title">PROGRESS:</div>
                      <div className="progress-item">
                        <span className="progress-label">Target:</span>
                        <span className="progress-value">{selectedMission.targetSystem}</span>
                      </div>
                    </div>
                  )}

                  <div className="missions-details-info">
                    <div className="info-row">
                      <span className="info-label">Time Remaining:</span>
                      <span 
                        className="info-value"
                        style={{ 
                          color: selectedMission.timeRemaining < 60 ? '#ff4444' : 
                                 selectedMission.timeRemaining < 180 ? '#ffaa00' : '#00ff88'
                        }}
                      >
                        {MissionManager.formatTimeRemaining(selectedMission.timeRemaining)}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Reward:</span>
                      <span className="info-value">{selectedMission.reward} CR</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Reputation:</span>
                      <span className="info-value">+{selectedMission.reputation}</span>
                    </div>
                  </div>

                  <div className="missions-details-actions">
                    <Button 
                      onClick={() => handleAbandonMission(selectedMission.id)}
                      variant="danger"
                    >
                      ABANDON MISSION
                    </Button>
                  </div>
                </>
              ) : (
                <div className="missions-details-empty">
                  <div className="empty-icon">📋</div>
                  <div className="empty-text">Select a mission to view details</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MissionsTab;
